import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BellRing, ExternalLink, LineChart, TrendingUp, TrendingDown, Minus } from "lucide-react";

const BAND_COLOR = { accelerating: "#10b981", steady: "#0ea5e9", cooling: "#f59e0b", stalled: "#ef4444" };
const BAND_PILL = {
  accelerating: "border-emerald-500 text-emerald-800 bg-emerald-50",
  steady: "border-sky-500 text-sky-800 bg-sky-50",
  cooling: "border-amber-500 text-amber-800 bg-amber-50",
  stalled: "border-rose-500 text-rose-800 bg-rose-50",
};

function TrendChart({ points, band = "steady" }) {
  if (!points || points.length < 2) {
    return (
      <div className="flex items-center justify-center h-48 border border-dashed border-border text-xs text-muted-foreground">
        Not enough data — capture pulse twice to see a trend.
      </div>
    );
  }
  const w = 620;
  const h = 200;
  const pad = 24;
  const scores = points.map(p => p.score);
  const min = Math.min(...scores, 0);
  const max = Math.max(...scores, 100);
  const range = max - min || 1;
  const step = (w - 2 * pad) / (points.length - 1);
  const coords = points.map((p, i) => [pad + i * step, h - pad - ((p.score - min) / range) * (h - 2 * pad)]);
  const path = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(" ");
  const area = `${path} L ${coords[coords.length - 1][0].toFixed(1)},${h - pad} L ${coords[0][0].toFixed(1)},${h - pad} Z`;
  const color = BAND_COLOR[band] || "#0f172a";

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-52 bg-white border border-border" data-testid="portfolio-drill-chart">
      {/* Band zones */}
      {[
        { from: 75, to: 100, color: "#10b98118" },
        { from: 55, to: 75,  color: "#0ea5e918" },
        { from: 35, to: 55,  color: "#f59e0b18" },
        { from: 0,  to: 35,  color: "#ef444418" },
      ].map((z, i) => {
        const y1 = h - pad - ((z.to - min) / range) * (h - 2 * pad);
        const y2 = h - pad - ((z.from - min) / range) * (h - 2 * pad);
        return <rect key={i} x={pad} y={Math.max(y1, pad)} width={w - 2 * pad} height={Math.max(0, y2 - Math.max(y1, pad))} fill={z.color} />;
      })}
      {/* Grid */}
      {[0, 25, 50, 75, 100].map((v) => {
        const y = h - pad - ((v - min) / range) * (h - 2 * pad);
        return (
          <g key={v}>
            <line x1={pad} y1={y} x2={w - pad} y2={y} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="2 3" />
            <text x={4} y={y + 3} fontSize="9" fill="#94a3b8">{v}</text>
          </g>
        );
      })}
      {/* Area */}
      <path d={area} fill={color} fillOpacity="0.12" />
      <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      {/* Points */}
      {coords.map((c, i) => (
        <circle key={i} cx={c[0]} cy={c[1]} r={3} fill={color}>
          <title>{`${points[i].score} · ${new Date(points[i].at).toLocaleDateString()}`}</title>
        </circle>
      ))}
    </svg>
  );
}

function DimensionMeter({ label, value, weight }) {
  const v = value ?? 0;
  const tone = v >= 75 ? "bg-emerald-500" : v >= 55 ? "bg-sky-500" : v >= 35 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="border border-border p-2 bg-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <div className="text-[10px] text-muted-foreground">{weight}%</div>
      </div>
      <div className="text-xl font-mono font-bold mt-1">{v}</div>
      <div className="h-1.5 bg-muted mt-1 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

export default function PortfolioBrandDrilldown({ wsId, brandId, onClose, onOpenAlertConfig }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertCfg, setAlertCfg] = useState(null);

  useEffect(() => {
    if (!wsId || !brandId) return;
    setLoading(true);
    Promise.all([
      api.get(`/workspaces/${wsId}/brands/${brandId}/pulse?limit=60`),
      api.get(`/workspaces/${wsId}/brands/${brandId}/pulse-alerts`),
    ])
      .then(([r1, r2]) => { setData(r1.data); setAlertCfg(r2.data); })
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load brand pulse")))
      .finally(() => setLoading(false));
  }, [wsId, brandId]);

  const trend = data?.trend || [];
  const latest = data?.latest || {};
  const brand = trend[trend.length - 1] || {};
  const band = data?.band || "steady";
  const scores = trend.map((r) => r.score);
  const prev = scores.length >= 2 ? scores[scores.length - 2] : null;
  const delta = prev !== null ? scores[scores.length - 1] - prev : 0;
  const min = scores.length ? Math.min(...scores) : 0;
  const max = scores.length ? Math.max(...scores) : 0;
  const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const DeltaIcon = delta > 2 ? TrendingUp : delta < -2 ? TrendingDown : Minus;
  const deltaTone = delta > 2 ? "text-emerald-700" : delta < -2 ? "text-rose-700" : "text-muted-foreground";

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="portfolio-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <LineChart className="w-4 h-4 text-primary" />
            <span>{brand.brand_name || latest.brand_name || "Brand"}</span>
            <Badge variant="outline" className={`rounded-none text-[10px] border ${BAND_PILL[band] || ""}`}>{band}</Badge>
            {data?.current !== null && data?.current !== undefined && (
              <span className="text-2xl font-mono font-black ml-2">{data.current}</span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                        onClick={() => onOpenAlertConfig({ id: brandId, name: brand.brand_name || latest.brand_name || "Brand" })}
                        data-testid="portfolio-drill-alerts-btn">
                <BellRing className="w-3 h-3 mr-1" /> Alerts
                {alertCfg?.enabled && <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-300 px-1">ON</span>}
              </Button>
              <Link to={`/app/brands/${brandId}`}>
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" data-testid="portfolio-drill-open-brand-btn">
                  <ExternalLink className="w-3 h-3 mr-1" /> Open brand
                </Button>
              </Link>
            </div>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>
        ) : trend.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">
            No pulse history yet. Capture pulse to start tracking.
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-2">
              <div className="border border-border p-2 bg-card">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Δ vs prev</div>
                <div className={`text-xl font-mono font-bold mt-1 inline-flex items-center gap-1 ${deltaTone}`}>
                  <DeltaIcon className="w-4 h-4" /> {delta > 0 ? "+" : ""}{delta}
                </div>
              </div>
              <div className="border border-border p-2 bg-card">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Range</div>
                <div className="text-xl font-mono font-bold mt-1">{min}–{max}</div>
              </div>
              <div className="border border-border p-2 bg-card">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Avg</div>
                <div className="text-xl font-mono font-bold mt-1">{avg}</div>
              </div>
              <div className="border border-border p-2 bg-card">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Data points</div>
                <div className="text-xl font-mono font-bold mt-1">{scores.length}</div>
              </div>
            </div>

            {/* Trend chart */}
            <TrendChart points={trend} band={band} />

            {/* Dimension breakdown */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Dimension breakdown (latest)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <DimensionMeter label="Health Δ" value={latest.dim_health} weight={40} />
                <DimensionMeter label="AI confidence" value={latest.dim_confidence} weight={20} />
                <DimensionMeter label="Activity" value={latest.dim_activity} weight={20} />
                <DimensionMeter label="Velocity" value={latest.dim_velocity} weight={20} />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
